import { useEffect } from "react";
import { AppState } from "react-native";

import { watchWebLocationPermission } from "@/lib/location-access";
import { useLocationAccessStore } from "@/state/location-access-store";
import { useStayVisibleStore } from "@/state/stay-visible-store";

/**
 * Keeps the shared location state honest: on launch, on every foreground (the
 * OS never tells an app that a permission changed in Settings), and when the
 * browser's own site controls change the answer.
 *
 * Runs above the tab tree rather than inside it, because losing location is
 * exactly what unmounts the tabs — a watcher living there would stop watching
 * at the moment it is needed.
 */
export function useLocationAccessWatch(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const refresh = () => {
      void (async () => {
        const access = await useLocationAccessStore.getState().refresh();
        if (access === "ready") return;

        // Permission gone or services off: tear Stay Visible down and mark the
        // user hidden, rather than leaving a venue presence nothing can update.
        void useStayVisibleStore.getState().verify();
      })();
    };

    refresh();

    const subscription = AppState.addEventListener("change", (next) => {
      if (next === "active") refresh();
    });

    const unwatchWeb = watchWebLocationPermission(refresh);

    return () => {
      subscription.remove();
      unwatchWeb();
    };
  }, [enabled]);
}
